import { useState, useEffect } from 'react'
import { 
  ArrowRight, Plus, Users, Plus as PlusIcon, Trash2, ArrowLeft, FileText,
  TrendingUp, Calendar, AlertCircle, Crown, Briefcase, Clock, CheckCircle,
  Download, Eye, Filter, Search, Activity, BarChart3, CreditCard,
  LayoutDashboard, Wallet, ShieldCheck, Settings, User, LogOut
} from 'lucide-react'
import TransactionsTable from '../components/TransactionsTable'
import LegalCompliance from '../components/LegalCompliance'
import ProfileBanner from '../components/ProfileBanner'
import LedgerOverlay from '../components/LedgerOverlay'
import DocumentApproval from '../components/DocumentApproval'
import Approvals from './Approvals'
import Billing from './Billing'
import InvestorPortal from './InvestorPortal'
import AuditLogs from './AuditLogs'
import LegalScreen from './LegalScreen'
import ProfileScreen from './ProfileScreen'
import Integrations from './Integrations'
import Brand from './Brand'
import Preferences from './Preferences'
import Discover from './Discover'
import ChatScreen from './ChatScreen'
import AgentDashboard from './AgentDashboard'
import { useLedger } from '../lib/LedgerContext'
import { legalCompliance, demoLegalCompliance } from '../data/seed'
import { getCountryDocumentsList } from '../data/country-documents'
import { vaultApi, documentsApi } from '../lib/api'
import { TasksTab } from './StaffWorkspace'
import { formatCurrency } from '../lib/format'
import { useAuth } from '../hooks/useAuth'
import { staffApi } from '../lib/api'
import { getPlan } from '../data/pricing'

// ✅ Dashboard components
import KpiCard from '../components/dashboard/KpiCard'
import ComplianceGauge from '../components/dashboard/ComplianceGauge'
import DocumentProgress from '../components/dashboard/DocumentProgress'
import QuickActions from '../components/dashboard/QuickActions'
import { Card, Badge, Button } from '../components/ui'

const TIMEFRAMES = ['Monthly', 'Quarterly', 'Yearly']
const SCALE = { Monthly: 1, Quarterly: 3, Yearly: 12 }

export default function AdminDashboard({
  activeTab,
  onNavigate,
  profileComplete,
  profile,
  isPremium,
  onSaveProfile,
  onUpgrade,
  country,
  currency = 'NGN',
  dateFormat = 'DD/MM/YYYY',
  companyName,
  onAgentRequest,
  demoMode = false,
  prefs,
  onSavePrefs,
}) {
  const tab = activeTab || 'dashboard'
  const [isPublic, setIsPublic] = useState(false)
  const { user, company: authCompany } = useAuth()
  const companyId = authCompany?.id
  const userId = user?.id

  // ✅ Route handlers - Profile is now handled here
  if (tab === 'profile') {
    return <ProfileScreen profile={profile} onSave={onSaveProfile} companyId={companyId} userId={userId} onNavigate={onNavigate} />
  }

  if (tab === 'audit-logs') return <AuditLogs onNavigate={onNavigate} />
  if (tab === 'messages') return <ChatScreen onNavigate={onNavigate} />
  if (tab === 'agent-projects' || tab === 'agent-chat' || tab === 'agent-profile' || tab === 'agent-upload')
    return <AgentDashboard activeTab={tab} onNavigate={onNavigate} />
  if (tab === 'legal') return <LegalScreen country={country} companyName={companyName} onAgentRequest={onAgentRequest} companyId={companyId} currentPlan={authCompany?.subscription_tier || 'growth'} onNavigateBilling={() => onNavigate?.('billing')} onNavigate={onNavigate} />
  if (tab === 'tokens' || tab === 'investor')
    return (
      <InvestorPortal
        compliance={legalCompliance}
        profileComplete={profileComplete}
        isPremium={isPremium}
        onUpgrade={onUpgrade}
        currency={currency}
        companyId={companyId}
        onNavigate={onNavigate}
      />
    )
  if (tab === 'integrations') return <Integrations onNavigate={onNavigate} />
  if (tab === 'preferences') return <Preferences prefs={prefs} onSave={onSavePrefs} onNavigate={onNavigate} />
  if (tab === 'approvals') return <Approvals companyId={companyId} onNavigate={onNavigate} />
  if (tab === 'tasks') return <AdminTasksPanel />
  if (tab === 'staff') return <StaffManagement companyId={companyId} />
  if (tab === 'reports') return <ReportsView currency={currency} onNavigate={onNavigate} />
  if (tab === 'ledger') return <LedgerRoute currency={currency} dateFormat={dateFormat} onNavigate={onNavigate} />
  if (tab === 'brand') return <Brand companyName={companyName} isPublic={isPublic} onTogglePublic={setIsPublic} companyId={companyId} onNavigate={onNavigate} />
  if (tab === 'billing') return <Billing currentPlan={authCompany?.subscription_tier || 'growth'} onNavigateBilling={() => {}} companyId={companyId} onNavigate={onNavigate} />
  if (tab === 'discover') return <Discover />
  if (tab === 'staff-approvals') return <StaffApprovalsPage onNavigate={onNavigate} />


  return (
    <DashboardView
      onNavigate={onNavigate}
      profileComplete={profileComplete}
      currency={currency}
      dateFormat={dateFormat}
      companyName={companyName}
      demoMode={demoMode}
      companyId={companyId}
      country={country}
    />
  )
}

// ============================================================
// MAIN DASHBOARD VIEW
// ============================================================

function DashboardView({ 
  onNavigate, 
  profileComplete, 
  currency, 
  dateFormat, 
  companyName, 
  demoMode, 
  companyId,
  country 
}) {
  const { kpis, transactions } = useLedger()
  const [timeframe, setTimeframe] = useState('Monthly')
  const [ledgerOpen, setLedgerOpen] = useState(false)
  const [docCount, setDocCount] = useState(0)
  const [docTotal, setDocTotal] = useState(0)
  const [docs, setDocs] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const { company: authCompany } = useAuth()

  // ✅ Get actual company name from auth
  const displayCompanyName = authCompany?.name || companyName || 'Your Company'

  // ✅ Get user's actual plan
  const userPlan = authCompany?.subscription_tier || 'seed'
  const userPlanData = getPlan(userPlan, country)

  // ✅ Get country document list
  const countryDocuments = getCountryDocumentsList(country)

  useEffect(() => {
    if (!companyId) {
      setDocTotal(countryDocuments.length)
      return
    }
    
    let mounted = true
    ;(async () => {
      const { data, error } = await documentsApi.list(companyId)
      if (!mounted) return
      
      if (!error && data && data.length > 0) {
        setDocs(data)
        setDocCount(data.length)
        setDocTotal(countryDocuments.length)
      } else {
        setDocs(countryDocuments.map(d => ({ ...d, status: 'missing', fileName: null })))
        setDocTotal(countryDocuments.length)
      }
    })()
    return () => { mounted = false }
  }, [companyId, countryDocuments])

  const healthScore = docTotal > 0 
    ? Math.round((docs.filter(d => ['uploaded', 'awaiting', 'verified'].includes(d.status)).length / docTotal) * 100)
    : 0

  const recent = transactions.slice(0, 6)
  const hasTransactions = transactions.length > 0

  const scaledKpis = kpis.map((k) => ({
    ...k,
    displayValue: formatCurrency(Math.round(k.value * SCALE[timeframe]), currency),
    delta: Math.round(k.delta * SCALE[timeframe]),
  }))

  // ✅ Quick Actions - Add Transaction is the first action
  const quickActions = [
    { 
      id: 'add-transaction', 
      label: 'Add Transaction', 
      icon: Plus, 
      color: 'bg-brand-soft text-brand',
      onClick: () => setShowAddModal(true) 
    },
    { 
      id: 'upload-document', 
      label: 'Upload Document', 
      icon: FileText, 
      color: 'bg-teal-soft text-teal',
      onClick: () => onNavigate?.('legal') 
    },
    { 
      id: 'generate-token', 
      label: 'Generate Token', 
      icon: 'Key', 
      color: 'bg-gold-soft text-gold',
      onClick: () => onNavigate?.('investor') 
    },
    { 
      id: 'view-investors', 
      label: 'Investor Portal', 
      icon: 'Crown', 
      color: 'bg-purple-soft text-brand',
      onClick: () => onNavigate?.('investor') 
    },
    { 
      id: 'view-ledger', 
      label: 'View Ledger', 
      icon: 'Wallet', 
      color: 'bg-blue-soft text-blue-600',
      onClick: () => setLedgerOpen(true) 
    },
  ]

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 sm:space-y-7 lg:space-y-8 animate-fade-in">
      
      {/* ============================================================
          HEADER - Clean with gradient accent
          ============================================================ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand/5 via-brand/10 to-transparent p-4 sm:p-6 border border-brand/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative z-10">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-ink flex items-center gap-2">
              <LayoutDashboard size={22} className="text-brand" />
              Dashboard
            </h1>
            <p className="text-xs sm:text-sm text-muted mt-0.5">
              Welcome back! Here's your governance overview for <span className="font-semibold text-ink">{displayCompanyName}</span>
            </p>
          </div>
          
          {/* ✅ Only Upload button in header */}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => onNavigate?.('legal')}
              className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5"
            >
              <FileText size={14} className="sm:mr-1" />
              <span className="hidden sm:inline">Upload Document</span>
              <span className="sm:hidden">Upload</span>
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================
          PROFILE BANNER (if incomplete)
          ============================================================ */}
      {!profileComplete && <ProfileBanner onNavigate={onNavigate} />}

      {/* ============================================================
          FINANCIAL KPIs - TOP
          ============================================================ */}
      {hasTransactions ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 flex items-center gap-2">
              <BarChart3 size={16} /> Financial Overview
            </h2>
            <div className="flex border border-neutral-200 bg-surface rounded-lg overflow-hidden">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf}
                  onClick={() => setTimeframe(tf)}
                  className={`px-2 sm:px-4 py-1 text-[10px] sm:text-xs font-semibold transition-colors ${
                    timeframe === tf ? 'bg-brand text-white' : 'text-neutral-500 hover:text-ink'
                  }`}
                >
                  {tf}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {scaledKpis.map((k) => (
              <KpiCard 
                key={k.id} 
                label={k.label} 
                value={k.displayValue} 
                delta={k.delta} 
                type={k.id}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="border border-neutral-200 rounded-xl p-8 sm:p-10 text-center bg-white">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <FileText size={24} className="text-neutral-400" />
          </div>
          <h3 className="text-sm sm:text-base font-semibold text-ink mb-2">No transactions yet</h3>
          <p className="text-xs sm:text-sm text-muted max-w-sm mx-auto">
            Record your first transaction to start tracking your financial activity.
          </p>
          <button 
            className="btn-primary mt-4 flex items-center gap-2 mx-auto text-sm"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} /> Add Transaction
          </button>
        </div>
      )}

      {/* ============================================================
          QUICK STATS - 4 compact cards
          ============================================================ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="p-3 sm:p-4 text-center">
          <p className="text-lg sm:text-2xl font-bold text-ink">{docTotal}</p>
          <p className="text-[10px] sm:text-xs text-muted">Total Docs</p>
        </Card>
        <Card className="p-3 sm:p-4 text-center border-l-4 border-l-success">
          <p className="text-lg sm:text-2xl font-bold text-success">{docCount}</p>
          <p className="text-[10px] sm:text-xs text-muted">Uploaded</p>
        </Card>
        <Card className="p-3 sm:p-4 text-center border-l-4 border-l-error">
          <p className="text-lg sm:text-2xl font-bold text-error">{docTotal - docCount}</p>
          <p className="text-[10px] sm:text-xs text-muted">Missing</p>
        </Card>
        <Card className="p-3 sm:p-4 text-center border-l-4 border-l-brand">
          <p className="text-lg sm:text-2xl font-bold text-brand">{healthScore}%</p>
          <p className="text-[10px] sm:text-xs text-muted">Health Score</p>
        </Card>
      </div>

      {/* ============================================================
          QUICK ACTIONS - INCLUDES Add Transaction
          ============================================================ */}
      <QuickActions actions={quickActions} />

      {/* ============================================================
          GOVERNANCE + DOCUMENT PROGRESS
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-1">
          <ComplianceGauge 
            score={healthScore} 
            label="Governance Health Score"
            size="md"
          />
        </div>
        <div className="lg:col-span-2">
          <DocumentProgress 
            documents={docs} 
            total={docTotal} 
            onViewAll={() => onNavigate?.('legal')} 
          />
        </div>
      </div>

      {/* ============================================================
          RECENT TRANSACTIONS + LEGAL COMPLIANCE
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2">
          {hasTransactions ? (
            <TransactionsTable 
              rows={recent} 
              onViewAll={() => setLedgerOpen(true)} 
            />
          ) : (
            <div className="border border-neutral-200 rounded-xl p-6 text-center bg-white">
              <p className="text-xs sm:text-sm text-muted">Add a transaction to see it here</p>
            </div>
          )}
        </div>
        <div className="space-y-4 sm:space-y-6">
          <LegalCompliance items={demoMode ? demoLegalCompliance : legalCompliance} />
          <InvestorPortalCard onNavigate={onNavigate} companyId={companyId} />
        </div>
      </div>

      {/* ============================================================
          LEDGER OVERLAY
          ============================================================ */}
      <LedgerOverlay 
        open={ledgerOpen || showAddModal} 
        onClose={() => { 
          setLedgerOpen(false); 
          setShowAddModal(false) 
        }} 
        role="admin" 
        currency={currency} 
        dateFormat={dateFormat} 
      />
    </div>
  )
}

// ============================================================
// LEDGER ROUTE
// ============================================================

function LedgerRoute({ currency, dateFormat, onNavigate }) {
  const [open, setOpen] = useState(true)
  
  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-ink flex items-center gap-2">
            <Wallet size={22} className="text-brand" />
            Ledger
          </h1>
          <p className="text-xs sm:text-sm text-muted mt-0.5">Full historical ledger with time filters.</p>
        </div>
        <button 
          onClick={() => { setOpen(false); onNavigate?.('dashboard') }}
          className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2"
        >
          <ArrowLeft size={14} /> Back
        </button>
      </div>
      
      <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
        <LedgerOverlay 
          open={open} 
          onClose={() => { setOpen(false); onNavigate?.('dashboard') }} 
          role="admin" 
          currency={currency} 
          dateFormat={dateFormat}
          isPageMode={true}
        />
      </div>
    </div>
  )
}

// ============================================================
// REPORTS VIEW
// ============================================================

function ReportsView({ currency, onNavigate }) {
  const { kpis } = useLedger()
  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-ink flex items-center gap-2">
            <BarChart3 size={22} className="text-brand" />
            Reports
          </h1>
          <p className="text-xs sm:text-sm text-muted mt-0.5">Financial summary across periods.</p>
        </div>
        <button onClick={() => onNavigate?.('dashboard')} className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2">
          <ArrowLeft size={14} /> Back
        </button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {kpis.map((k) => (
          <div key={k.id} className="border border-neutral-200 rounded-xl p-4 sm:p-5 bg-white">
            <p className="text-xs text-muted">{k.label}</p>
            <p className="text-xl sm:text-2xl font-bold text-ink mt-1">{formatCurrency(k.value, currency)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================
// STAFF APPROVAL
//=============================

function StaffApprovalsPage({ onNavigate }) {
  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Staff Approvals</h1>
        <p className="text-sm text-muted mt-1">Review and approve staff-submitted documents and requests.</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <DocumentApproval />
        </div>
        <div className="space-y-6">
          <div className="surface p-6">
            <h3 className="text-sm font-semibold text-ink mb-2">Pending Reviews</h3>
            <p className="text-2xl font-bold text-brand">0</p>
            <p className="text-xs text-muted mt-1">Documents waiting for your review</p>
          </div>
          <div className="surface p-6">
            <h3 className="text-sm font-semibold text-ink mb-2">Recent Activity</h3>
            <p className="text-xs text-muted">No recent staff activity</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// STAFF MANAGEMENT
// ============================================================

function StaffManagement({ companyId }) {
  const [staff, setStaff] = useState([])
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!companyId) { setLoading(false); return }
    let mounted = true
    ;(async () => {
      const { data, error } = await staffApi.list(companyId)
      if (!mounted) return
      if (!error && data) setStaff(data)
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [companyId])

  const addStaff = async (e) => {
    e.preventDefault()
    if (!name || !email || !companyId) return
    const { data, error } = await staffApi.create({ company_id: companyId, name, email, role: 'staff', permissions: ['read'] })
    if (error) return
    setStaff((prev) => [...prev, data])
    setName(''); setEmail('')
  }

  const removeStaff = async (id) => {
    setStaff((prev) => prev.filter((s) => s.id !== id))
    await staffApi.remove(id)
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-ink flex items-center gap-2">
          <Users size={22} className="text-brand" />
          Staff Management
        </h1>
        <p className="text-xs sm:text-sm text-muted mt-0.5">Manage your team members and their access.</p>
      </div>

      <form onSubmit={addStaff} className="border border-neutral-200 rounded-xl p-4 sm:p-6 bg-white space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-3 sm:items-end">
        <div className="flex-1 min-w-[140px] sm:min-w-[180px]">
          <label className="label-above text-[10px] sm:text-xs">Name</label>
          <input 
            type="text" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            className="input-flat min-h-[40px] sm:min-h-[44px] text-sm" 
            placeholder="Full name" 
          />
        </div>
        <div className="flex-1 min-w-[140px] sm:min-w-[180px]">
          <label className="label-above text-[10px] sm:text-xs">Email</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            className="input-flat min-h-[40px] sm:min-h-[44px] text-sm" 
            placeholder="email@company.com" 
          />
        </div>
        <button 
          type="submit" 
          className="btn-primary text-xs flex items-center gap-1.5 min-h-[40px] sm:min-h-[44px] px-4 w-full sm:w-auto"
        >
          <PlusIcon size={14} /> Add Staff
        </button>
      </form>

      <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <div className="min-w-[500px] sm:min-w-[600px]">
            <div className="grid grid-cols-12 px-4 sm:px-6 py-3 border-b border-neutral-200 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
              <div className="col-span-4">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            <div className="divide-y divide-neutral-100">
              {loading && (
                <div className="px-4 sm:px-6 py-8 sm:py-10 text-center text-sm text-muted">Loading staff...</div>
              )}
              {!loading && staff.length === 0 && (
                <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
                  <Users size={24} className="text-neutral-300 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-ink">No staff members yet</p>
                  <p className="text-xs text-muted mt-1">Invite team members using the form above.</p>
                </div>
              )}
              {!loading && staff.map((s) => (
                <div key={s.id} className="grid grid-cols-12 px-4 sm:px-6 py-3 text-sm items-center">
                  <div className="col-span-4 text-ink truncate">{s.name}</div>
                  <div className="col-span-4 text-muted truncate text-xs sm:text-sm">{s.email}</div>
                  <div className="col-span-3 text-muted text-xs sm:text-sm">{s.role}</div>
                  <div className="col-span-1 text-right">
                    <button 
                      onClick={() => removeStaff(s.id)} 
                      className="text-muted hover:text-error transition-colors p-1" 
                      aria-label={`Remove ${s.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// TASKS PANEL
// ============================================================

function AdminTasksPanel() {
  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-fade-in">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-ink flex items-center gap-2">
          <CheckCircle size={22} className="text-brand" />
          Compliance Tasks
        </h1>
        <p className="text-xs sm:text-sm text-muted mt-0.5">Assign and manage compliance tasks for your team.</p>
      </div>
      <div className="border border-neutral-200 rounded-xl overflow-hidden bg-white p-4 sm:p-6">
        <TasksTab isAdmin={true} />
      </div>
    </div>
  )
}

// ============================================================
// INVESTOR PORTAL CARD
// ============================================================

function InvestorPortalCard({ onNavigate, companyId }) {
  const [tokenCount, setTokenCount] = useState(0)
  const [lastAccessed, setLastAccessed] = useState('—')

  useEffect(() => {
    if (!companyId) return
    let mounted = true
    ;(async () => {
      const { data, error } = await vaultApi.listTokens(companyId)
      if (!mounted || error || !data) return
      setTokenCount(data.length)
      if (data.length > 0) {
        const latest = data[0]
        setLastAccessed((latest.created_at || '').split('T')[0] || '—')
      }
    })()
    return () => { mounted = false }
  }, [companyId])

  return (
    <div className="border border-neutral-200 rounded-xl p-4 sm:p-5 bg-white">
      <div className="flex items-center gap-2 mb-2">
        <Crown size={16} className="text-brand" />
        <h3 className="text-sm font-semibold text-ink">Investor Portal</h3>
      </div>
      <div className="flex items-center justify-between text-xs text-muted mb-4">
        <span>Active Links: <span className="font-mono text-ink">{tokenCount}</span></span>
        <span>Last accessed: <span className="font-mono text-ink">{lastAccessed}</span></span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button 
          className="btn-secondary text-xs flex-1 min-h-[36px]" 
          onClick={() => onNavigate?.('investor')}
        >
          Manage Access
        </button>
        <button 
          className="btn-primary text-xs flex-1 min-h-[36px]" 
          onClick={() => onNavigate?.('investor')}
        >
          Generate
        </button>
      </div>
    </div>
  )
}